import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import * as SolidIcons from '@heroicons/react/24/solid';

const StatCard = ({ iconName, value, title, isLoading }) => {
    const IconComponent = SolidIcons[iconName] || SolidIcons.QuestionMarkCircleIcon;
    
    if (isLoading) {
        return (
            <div className="text-center p-4 bg-gray-100 dark:bg-gray-700/50 rounded-lg animate-pulse">
                <div className="h-12 w-12 mx-auto bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                <div className="h-8 w-24 mt-2 mx-auto bg-gray-300 dark:bg-gray-600 rounded"></div>
                <div className="h-6 w-32 mt-2 mx-auto bg-gray-300 dark:bg-gray-600 rounded"></div>
            </div>
        );
    }

    return (
        <div className="text-center">
            <IconComponent className="w-12 h-12 mx-auto text-orange-500" />
            <p className="mt-2 text-4xl font-extrabold text-gray-900 dark:text-white">{value.toLocaleString()}+</p>
            <p className="text-lg text-gray-500 dark:text-gray-400">{title}</p>
        </div>
    );
};

const DonationGoal = ({ goal, isLoading }) => {
    const brandColor = '#e46809';
    
    if (isLoading) {
        return (
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl shadow-xl p-8 mb-12 border border-orange-100 dark:border-gray-600 animate-pulse">
                <div className="h-8 w-64 bg-gray-300 dark:bg-gray-600 rounded mb-4 mx-auto"></div>
                <div className="h-4 w-96 bg-gray-300 dark:bg-gray-600 rounded mb-6 mx-auto"></div>
                <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                <div className="h-4 w-48 bg-gray-300 dark:bg-gray-600 rounded mx-auto"></div>
            </div>
        );
    }

    if (!goal || !goal.is_active) return null;

    const progress = (goal.current_amount / goal.target_amount) * 100;
    const itemsNeeded = goal.target_amount - goal.current_amount;

    return (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl shadow-xl p-8 mb-12 border border-orange-100 dark:border-gray-600">
            {/* Header with community-focused messaging */}
            <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                    Together, We're Making a Difference
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                    This isn't just our goal—it's <span className="font-semibold text-orange-600 dark:text-orange-400">our community's achievement</span>. 
                    Every donation brings us closer to changing lives through assistive technology.
                </p>
            </div>

            {/* Progress Section */}
            <div className="max-w-4xl mx-auto">
                {/* Goal Header */}
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                        Community Goal: {goal.title} by {goal.target_year}
                    </h3>
                    <span className="text-lg font-bold text-orange-600 dark:text-orange-400">
                        {goal.current_amount.toLocaleString()} / {goal.target_amount.toLocaleString()}
                    </span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-6 mb-4 overflow-hidden">
                    <div 
                        className="h-6 rounded-full transition-all duration-1000 ease-out relative"
                        style={{ 
                            width: `${Math.min(progress, 100)}%`,
                            backgroundColor: brandColor
                        }}
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20"></div>
                    </div>
                </div>

                {/* Progress Message */}
                <div className="text-center">
                    <p className="text-gray-600 dark:text-gray-300 mb-2">
                        {progress >= 100 ? (
                            <span className="font-bold text-green-600 dark:text-green-400">
                                🎉 Amazing! Our community has reached the goal! Thank you!
                            </span>
                        ) : (
                            <>
                                <span className="font-semibold text-orange-600 dark:text-orange-400">
                                    Only {itemsNeeded.toLocaleString()} items needed
                                </span>
                                {' '}to reach our {goal.target_year} goal
                            </>
                        )}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Powered by generous donors like you • {Math.round(progress)}% of goal achieved
                    </p>
                </div>

                {/* Call to Action */}
                {progress < 100 && (
                    <div className="text-center mt-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-orange-200 dark:border-gray-600">
                        <p className="text-gray-700 dark:text-gray-300 mb-3">
                            <strong>Join our community of changemakers!</strong> Your contribution helps us get closer to our goal.
                        </p>
                        <Link 
                            to="/campaigns"
                            className="inline-block px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg transition-colors duration-200"
                        >
                            Contribute to Our Goal
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default function StatsCounter() {
    const [stats, setStats] = useState([]);
    const [donationGoal, setDonationGoal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [goalLoading, setGoalLoading] = useState(true);
    const brandColor = '#e46809';

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch stats
                const statsResponse = await fetch(`${process.env.REACT_APP_API_URL}/stats`);
                if (!statsResponse.ok) throw new Error('Failed to load stats');
                const statsData = await statsResponse.json();
                setStats(statsData);

                // Fetch donation goal
                const goalResponse = await fetch(`${process.env.REACT_APP_API_URL}/stats/donation-goal`);
                if (goalResponse.ok) {
                    const goalData = await goalResponse.json();
                    setDonationGoal(goalData);
                }
            } catch (error) {
                console.error(error);
                setStats([]);
                setDonationGoal(null);
            } finally {
                setLoading(false);
                setGoalLoading(false);
            }
        };
        fetchData();
    }, []);

    if (!loading && stats.length === 0 && !donationGoal) {
        return null; 
    }

    return (
        <section className="bg-gray-50 dark:bg-gray-800/50 py-20">
            <div className="container mx-auto px-4">
                {/* Donation Goal - Placed ABOVE the stats */}
                <DonationGoal goal={donationGoal} isLoading={goalLoading} />

                {/* Stats Header */}
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                        Our Impact at a Glance
                    </h2>
                    <p className="text-lg text-gray-600 dark:text-gray-400">
                        Every gift contributes to a growing movement of empowerment and accessibility.
                    </p>
                    <div className="w-24 h-1 mx-auto mt-6" style={{ backgroundColor: brandColor }} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {loading ? (
                        <>
                            <StatCard isLoading={true} />
                            <StatCard isLoading={true} />
                            <StatCard isLoading={true} />
                        </>
                    ) : (
                        stats.map(stat => (
                            <StatCard 
                                key={stat.title} 
                                iconName={stat.icon_name} 
                                value={stat.stat_value} 
                                title={stat.title} 
                            />
                        ))
                    )}
                </div>
            </div>
        </section>
    );
}